import { animate, state, style, transition, trigger } from "@angular/animations";
import {
	AfterContentInit,
	Component,
	EventEmitter,
	HostListener,
	Inject,
	Input,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { DateAdapter } from "@angular/material/core";
import {
	DateRange,
	DefaultMatCalendarRangeStrategy,
	MAT_DATE_RANGE_SELECTION_STRATEGY,
	MatCalendar,
	MatCalendarCellCssClasses,
} from "@angular/material/datepicker";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { isValidDate } from "@app/_helpers/date-fns";
import { createRxValue, fromRxValue } from "@app/_helpers/utils";
import { TranslateService } from "@ngx-translate/core";
import { Logger, UserSettingsQuery } from "@timeghost/web-api";
import {
	differenceInDays,
	endOfDay,
	endOfWeek,
	isSameDay,
	isValid,
	parse,
	startOfDay,
	startOfMonth,
	startOfWeek,
} from "date-fns/esm";
import { orderBy } from "lodash";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { distinctUntilChanged, filter, map, startWith } from "rxjs/operators";
import { CalRangeType, TimeRangeStrategyService } from "../customRangeAdapter";
import {
	applyTimeRangeAfterParse,
	getTimeRangeByType,
	getTimeRangeNameForStrategy,
	getTimeRangePresetMetadata,
	TIME_RANGE_CUSTOM_NAME,
	TRange,
	TRangePresets,
} from "../time-range-constants";
import { CalendarRangeConfig } from "./calendar-range-picker.service";
import { CalendarRangePickerOverlayRef } from "./calendar-range-picker-ref";
import { CALENDAR_RANGE_DIALOG_DATA } from "./calendar-range-picker-ref-tokens";

const ESCAPE = 27;
const ANIMATION_TIMINGS_IN = "300ms cubic-bezier(0.25, 1.25, 0.25, 1)";
const ANIMATION_TIMINGS_OUT = "300ms cubic-bezier(0.25, 0.85, 0.25, 1)";
const log = new Logger("CalendarRangePickerComponent");

@Component({
	selector: "app-calendar-range-picker",
	templateUrl: "./calendar-range-picker.component.html",
	styleUrls: ["./calendar-range-picker.component.scss"],
	providers: [DefaultMatCalendarRangeStrategy],
	animations: [
		trigger("fade", [
			state("fadeOut", style({ opacity: 0 })),
			state("fadeIn", style({ opacity: 1 })),
			transition("* => fadeIn", animate(ANIMATION_TIMINGS_IN)),
		]),
		trigger("slideContent", [
			state("void", style({ transform: "translate3d(0, 0, 0) scale(0.65)", opacity: 0 })),
			state("enter", style({ transform: "none", opacity: 1 })),
			state("leave", style({ transform: "translate3d(0, 0, 0) scale(0.65)", opacity: 0 })),
			transition("* => *", animate(ANIMATION_TIMINGS_IN)),
		]),
	],
})
export class CalendarRangePickerComponent implements OnInit, OnDestroy, AfterContentInit {
	loading = true;
	animationState: "void" | "enter" | "leave" = "enter";
	animationStateChanged = new EventEmitter<AnimationEvent>();
	private destroy$ = new Subject<void>();
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
	@ViewChild("startCal", { static: true })
	private startCal: MatCalendar<Date>;
	@ViewChild("endCal", { static: true })
	private endCal: MatCalendar<Date>;
	private ranges = new BehaviorSubject<TRangePresets>(this.config.presets);
	readonly rangeCustomName = TIME_RANGE_CUSTOM_NAME;
	readonly ranges$ = this.ranges.asObservable().pipe(distinctUntilChanged());
	private range = new BehaviorSubject<TRange>(null);
	readonly minDate = createRxValue<Date>(null);
	readonly maxDate = createRxValue<Date>(null);
	private get rangeNative() {
		return new DateRange(this.range.value.from, this.range.value.to);
	}
	readonly range$ = this.range.asObservable().pipe(distinctUntilChanged());
	readonly range$SameDay = this.range$.pipe(map((x) => isSameDay(x.from, x.to)));
	readonly range$Date = this.range$.pipe(
		map((x) => {
			if (!x) return new DateRange(null, null);
			return new DateRange(x.from, x.to);
		})
	);
	readonly calendarValue = fromRxValue(this.range$Date, new DateRange(null, null));
	readonly rangeStart$ = this.range$.pipe(
		filter((x) => !!x?.from && isValid(x.from)),
		map((x) => x.from)
	);
	readonly rangeEnd$ = this.range$.pipe(
		filter((x) => !!x?.to && isValid(x.to)),
		map((x) => x.to)
	);

	readonly startDatePlaceholder = createRxValue<string>("");
	readonly endDatePlaceholder = createRxValue<string>("");
	private _customRanges = new BehaviorSubject<TRangePresets>(null);
	readonly customRanges$ = this._customRanges.asObservable().pipe(distinctUntilChanged());
	@Input()
	get customRanges() {
		return this._customRanges.getValue();
	}
	set customRanges(val: TRangePresets) {
		this._customRanges.next(val);
	}
	readonly ranges$Custom = combineLatest([this.ranges$.pipe(startWith()), this.customRanges$.pipe(startWith())]).pipe(
		map(([presets, customPresets]) => {
			if (!customPresets) {
				return presets;
			} else if (!presets) {
				return customPresets;
			}
			return { ...presets, ...customPresets };
		}),
		map((x) => {
			return orderBy(x, (y) => y.indexName);
		})
	);
	readonly rangeStrategy$ = createRxValue<CalRangeType>();
	readonly allowDynamic = createRxValue<boolean>(false);
	readonly allowGeneralize = createRxValue<boolean>(false);
	@HostListener("document:keydown.escape", ["$event"])
	private handleKeydown(event: KeyboardEvent) {
		this.dialogRef.close();
	}
	constructor(
		private dialog: MatDialog,
		private translateService: TranslateService,
		public dialogRef: CalendarRangePickerOverlayRef,
		@Inject(CALENDAR_RANGE_DIALOG_DATA)
		private config: CalendarRangeConfig,
		private userSettings: UserSettingsQuery,
		private dateAdapter: DateAdapter<any>
	) {
		this.ranges.next(this.config.presets);
		this.allowDynamic.next(this.config.allowDynamic);
		this.allowGeneralize.next(this.config.allowGeneralize);
		const userLanguage = this.userSettings.getValue().settings.languageSetting;
		this.dateAdapter.setLocale(userLanguage);
	}
	onLoad(event: Event) {
		this.loading = false;
	}

	ngOnInit() {
		this.maxDate.next(this.config?.maxDate);
		this.minDate.next(this.config?.minDate);
		this.selectRange(this.config.selected, true);
	}
	ngAfterContentInit() {}
	onAnimationStart(event: AnimationEvent) {
		this.animationStateChanged.emit(event);
	}

	onAnimationDone(event: AnimationEvent) {
		this.animationStateChanged.emit(event);
	}
	get currentLang() {
		return this.translateService.currentLang;
	}
	startExitAnimation() {
		this.animationState = "leave";
	}

	selectRange(ev: TRange, triggerUpdateActiveDate: boolean = true) {
		if (!ev.rangeType) {
			ev = { ...ev, rangeType: null, name: TIME_RANGE_CUSTOM_NAME };
		} else if (ev.rangeType === "flex" && !ev.from) {
			ev.from = this.startDate.value;
		}
		this.setRangeConfig(ev.rangeType);

		this.range.next(ev);

		this.errorValue.next(null);
		if (ev.from < this.minDate.value) {
			ev.from = this.minDate.value;
			this.errorValue.next("startDateWasAdjusted");
		} else if (this.maxDate.value && ev.from > this.maxDate.value) {
			ev.from = this.maxDate.value;
			this.errorValue.next("startDateWasAdjusted");
		}
		if (this.maxDate.value && ev.to > this.maxDate.value) {
			if (this.errorValue.value === "startDateWasAdjusted") this.errorValue.next("bothDatesAdjusted");
			else this.errorValue.next("endDateWasAdjusted");
			ev.to = endOfDay(this.maxDate.value);
		} else if (ev.to < this.minDate.value) {
			if (this.errorValue.value === "startDateWasAdjusted") this.errorValue.next("bothDatesAdjusted");
			else this.errorValue.next("endDateWasAdjusted");
			ev.to = endOfDay(this.minDate.value);
		}
		this.errorValue.value;

		this.startDate.patchValue(ev.from);
		this.endDate.patchValue(ev.to);

		this.placeholderUpdate(ev);

		if (triggerUpdateActiveDate) {
			try {
				this.updateActiveDate(ev);
			} catch (e) {
				log.error(e);
			}
		}
	}
	updateActiveDate(ev?: TRange) {
		log.debug(ev || this.range.value, this.startCal);
		if (this.startCal) {
			ev = ev ?? this.range.value;
			this.startCal.startAt = this.startCal.activeDate = startOfMonth(ev.from.getTime());
			this.startCal.stateChanges.next();
			// try {
			//   this.startCal.focusActiveCell();
			//   this.endCal.focusActiveCell();
			// } catch {}
		}
		if (this.endCal) {
			ev = ev ?? this.range.value;
			this.endCal.startAt = this.endCal.activeDate = startOfMonth(ev.to.getTime());
			this.endCal.stateChanges.next();
		}
	}

	readonly usersDateFormat = createRxValue(this.userSettings.getValue().settings.dateFormat);
	readonly errorValue = createRxValue(null);
	startDate = new FormControl<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
	endDate = new FormControl<Date>(endOfWeek(new Date(), { weekStartsOn: 1 }));

	selectedChange(ev: any, start: boolean) {
		const user = this.userSettings.getValue();
		if (typeof ev === "string") {
			const newDate = parse(ev, user.settings.dateFormat, new Date());
			if (!isValidDate(newDate)) return;
			ev = newDate;
		}
		const newRange = start
			? this.changeStart(ev, this.calendarValue.value)
			: this.changeEnd(ev, this.calendarValue.value);
		this.startDate.patchValue(newRange.start);
		if (newRange.end) this.endDate.patchValue(newRange.end);
		if (!newRange.end || !this.rangeStrategy$.value) {
			this.calendarValue.update(newRange);
			if (newRange.end) this.selectRange(this.buildRangeFromCalendarChange(newRange, true));
			return;
		}
		if (newRange.end) {
			this.selectRange(this.buildRangeFromCalendarChange(newRange, false));
		}
		this.startCal.stateChanges.next();
		this.endCal.stateChanges.next();
	}
	saveSelection() {
		this.dialogRef.close(applyTimeRangeAfterParse(this.range.getValue()));
	}
	cancel() {
		this.dialogRef.close();
	}

	dtRange() {
		return (date: Date): MatCalendarCellCssClasses => {
			const range = this.range ? this.range.getValue() : null;
			let isRange = false;
			if (range && differenceInDays(range.from, range.to) === 0) {
				return;
			}
			if (range && (isRange = differenceInDays(range.from, range.to) > 0)) {
				log.debug(isRange, date);
				return "mat-date-range-inBetween";
			}
			if (range && (isRange = isSameDay(range.from, Date.now()))) {
				log.debug(isRange, date);
				return "mat-date-range-isStart";
			}
			if (range && (isRange = isSameDay(range.to, Date.now()))) {
				log.debug(isRange, date);
				return "mat-date-range-isEnd";
			}
		};
	}
	private rangeStrategy = inject(MAT_DATE_RANGE_SELECTION_STRATEGY) as TimeRangeStrategyService<Date>;
	setRangeConfig(type: CalRangeType) {
		this.rangeStrategy$.next(type);
		this.startCal.stateChanges.next();
	}

	private buildRangeFromCalendarChange(newRange: DateRange<Date>, useFormControls: boolean): TRange {
		const strategyType = this.rangeStrategy$.value;
		const strategyPreset = strategyType ? getTimeRangeByType(strategyType) : undefined;
		const from = useFormControls ? this.startDate.getRawValue() : newRange.start;
		const to = useFormControls ? this.endDate.getRawValue() : newRange.end;
		return {
			...this.range.value,
			...getTimeRangePresetMetadata(strategyPreset),
			name: getTimeRangeNameForStrategy(strategyType),
			from: isValid(from) ? startOfDay(from) : null,
			to: isValid(to) ? endOfDay(to) : null,
			rangeType: strategyType,
		};
	}

	changeStart(date: Date, currentRange: DateRange<Date>) {
		if (isValidDate(date)) {
			this.setRangeConfig(this.rangeStrategy$.value == "flex" ? "flex" : null);
			return new DateRange(date, currentRange.end);
		}
		return new DateRange(currentRange.start, currentRange.end);
	}
	changeEnd(date: Date, currentRange: DateRange<Date>) {
		if (isValidDate(date)) {
			this.setRangeConfig(this.rangeStrategy$.value == "flex" ? null : null);
			return new DateRange(currentRange.start, date);
		}
		return new DateRange(currentRange.start, currentRange.end);
	}
	placeholderUpdate(ev: TRange) {
		switch (ev.name) {
			case "time-range.preset.day": {
				this.startDatePlaceholder.next(this.translateService.instant(ev.name));
				this.endDatePlaceholder.next(this.translateService.instant(ev.name));
				break;
			}
			case "time-range.preset.week": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.week-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.week-end"));
				break;
			}
			case "time-range.preset.month": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.month-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.month-end"));
				break;
			}
			case "time-range.preset.quarter": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.quarter-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.quarter-end"));
				break;
			}
			case "time-range.preset.year": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.year-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.year-end"));
				break;
			}
			case "time-range.preset.yesterday": {
				this.startDatePlaceholder.next(this.translateService.instant(ev.name));
				this.endDatePlaceholder.next(this.translateService.instant(ev.name));
				break;
			}
			case "time-range.preset.last-week": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-week-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-week-end"));
				break;
			}
			case "time-range.preset.last-month": {
				this.startDatePlaceholder.next(
					this.translateService.instant("time-range.placeholder.last-month-start")
				);
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-month-end"));
				break;
			}
			case "time-range.preset.last-quarter": {
				this.startDatePlaceholder.next(
					this.translateService.instant("time-range.placeholder.last-quarter-start")
				);
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-quarter-end"));
				break;
			}
			case "time-range.preset.last-year": {
				this.startDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-year-start"));
				this.endDatePlaceholder.next(this.translateService.instant("time-range.placeholder.last-year-end"));
				break;
			}
			case "time-range.preset.custom": {
				return;
			}
			case "time-range.preset.until-today": {
				this.endDatePlaceholder.next(this.translateService.instant("time-range.preset.day"));
				this.endDate.patchValue(undefined);
				return;
			}
		}
		this.startDate.patchValue(undefined);
		this.endDate.patchValue(undefined);
	}

	isToday(date: any) {
		return isSameDay(date.end, new Date());
	}
}
